import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

/**
 * Workflow step: transform `rexel_initinfc` (fileVariant=initinfcs) raw rows
 * to deactivate superseded products in `im_products_v2`.
 *
 * Stage 2 of the INITINFCS ingest pipeline. Runs after `master_data_bulk_insert`
 * has populated `rexel_initinfc` with fileVariant=initinfcs rows. For each row
 * in the latest batch:
 *   - Resolves SKU → `im_products_v2.id`
 *   - Marks the matching product as inactive
 *   - Skips rows where the SKU cannot be resolved
 *
 * Scoping: `updatedAfter` (typically wired from upstream `startedAt`) limits
 * the rexel_initinfc rows considered to those touched by the current run.
 */
export const transformInitinfcsProductsStep = defineStepConfig({
  type: "transform_initinfcs_products",
  name: "Transform INITINFCS → Deactivate Superseded Products",
  description:
    "Reads rexel_initinfc rows (fileVariant=initinfcs) updated by the current run and marks matching im_products_v2 entries as inactive.",
  category: "Transformation",
  libraryModule: "Procurement",
  inputSchema: z.object({
    updatedAfter: z
      .string()
      .optional()
      .describe(
        "ISO timestamp — only process rows updated after this time. " +
          "Typically wired from the upstream master_data_bulk_insert's startedAt output.",
      ),
  }),
  outputSchema: z.object({
    totalRows: z
      .number()
      .describe("Total number of rexel_initinfc (initinfcs) rows processed"),
    deactivated: z
      .number()
      .describe("Number of im_products_v2 rows marked as inactive"),
    alreadyInactive: z
      .number()
      .describe(
        "Number of products that were already inactive (no change needed)",
      ),
    skippedUnresolved: z
      .number()
      .describe(
        "Number of rows skipped because the SKU could not be resolved in im_products_v2",
      ),
    updatedAfter: z
      .string()
      .optional()
      .describe(
        "Pass-through of the input timestamp so downstream steps can scope to the current run.",
      ),
  }),
  configSchema: z.object({
    batchSize: z
      .number()
      .min(1)
      .max(5000)
      .prefault(500)
      .describe("Number of rows to process per batch"),
  }),
  emits: [] as const,
  execution: {
    startToCloseTimeout: "30 minutes",
    retry: { maximumAttempts: 2 },
  },
});
